import { useState, useEffect, useRef } from "react";
import { Sidebar as ProSidebar, Menu, MenuItem, SubMenu } from "react-pro-sidebar";
import { Box, Typography, useTheme, IconButton, useMediaQuery } from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  HomeOutlined,
  PersonOutlined,
  PeopleOutlined,
  FamilyRestroom,
  ClassOutlined,
  MenuBook,
  Assignment,
  CalendarToday,
  CalendarMonth,
  AccessTime,
  Cancel,
  AdminPanelSettingsOutlined,
  VpnKeyOutlined,
  GroupsOutlined,
  DesignServicesOutlined,
  PaymentsOutlined,
  AppRegistrationOutlined,
  SettingsOutlined,
  HistoryOutlined,
} from "@mui/icons-material";
import { Link, useLocation } from "react-router-dom";
import {
  PERMISSIONS,
  profileHasTenantServiceCode,
} from "platform/rbac/Permissions";
import { SERVICE_CODES } from "shared/constants/serviceCodes";
import { tokens } from "shared/theme";
import { useAuth } from "shared/hooks/useAuth";
import useProfilePhotoSrc from "shared/hooks/useProfilePhotoSrc";
import { useTenantPath } from "shared/hooks/useTenantPath";
import { stripTenantPrefixFromPathname } from "shared/utils/tenantPath";
import {
  TENANT_ADMIN_PERMISSION,
  sessionHasPermission,
} from "shared/utils/sessionPermissions";

/** Sidebar width; lg+ uses in-flow flex — keep Box + ProSidebar in sync */
export const SIDEBAR_WIDTH_PX = 220;

const Item = ({ title, to, icon, selected, setSelected, closeSidebar, linkState }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const handleClick = () => {
    setSelected(title);
    closeSidebar();
  };

  return (
    <MenuItem
      active={selected === title}
      style={{ color: colors.grey[100] }}
      onClick={handleClick}
      icon={icon}
      component={<Link to={to} state={linkState} />}
    >
      <Typography
        component="span"
        sx={{
          flex: 1,
          minWidth: 0,
          fontSize: "0.8rem",
          lineHeight: 1.35,
          whiteSpace: "normal",
          wordBreak: "break-word",
          display: "block",
          textAlign: "left",
          color: "inherit",
        }}
      >
        {title}
      </Typography>
    </MenuItem>
  );
};

const SidebarComponent = ({ isSidebarOpen, setIsSidebarOpen }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const primaryMain = theme.palette.primary.main;
  const secondaryMain = theme.palette.secondary.main;
  const selectedText = theme.palette.secondary.contrastText;
  const location = useLocation();
  const { tp } = useTenantPath();
  const isDesktopSidebar = useMediaQuery(theme.breakpoints.up("md"));
  const [selected, setSelected] = useState("Dashboard");
  const { user } = useAuth();
  const profile = user?.data;
  const role = profile?.role;
  const normalizedRole = String(role ?? "")
    .trim()
    .toUpperCase()
    .replace(/[\s_-]+/g, "");
  const tenantPhoto = profile?.tenantBranding?.tenantPhoto;
  const tenantLogoSrc = useProfilePhotoSrc(tenantPhoto);
  const tenantDisplayName =
    profile?.tenantName != null && String(profile.tenantName).trim() !== ""
      ? String(profile.tenantName).trim()
      : profile?.tenantBranding?.name != null &&
          String(profile.tenantBranding.name).trim() !== ""
        ? String(profile.tenantBranding.name).trim()
        : "Datahouse";
  /** Registry tenant UUID from session (same as Redis-backed auth context / X-TenantID). */
  const registryTenantId =
    profile?.tenantId != null ? String(profile.tenantId).trim() : "";
  const hasRegistryTenant = registryTenantId.length > 0;
  const tenantUsersLinkState =
    profile?.tenantName != null && String(profile.tenantName).trim() !== ""
      ? { tenantName: String(profile.tenantName).trim() }
      : undefined;
  const canSeePayroll = profileHasTenantServiceCode(profile, SERVICE_CODES.PAYROLL);
  const canSeeContract = profileHasTenantServiceCode(profile, SERVICE_CODES.CONTRACT);
  const canSeeSystemRegisterService = profileHasTenantServiceCode(
    profile,
    SERVICE_CODES.SYSTEM_REGISTER
  );
  const canBypassSystemRegisterPermission =
    normalizedRole === "ADMIN"
    || normalizedRole === "SYSTEMADMIN"
    || normalizedRole === "SERVICEADMIN";
  const itGovernanceViewPermissions = [
    PERMISSIONS.systemRegister.viewSystems,
    PERMISSIONS.systemRegister.viewSystemDetails,
    PERMISSIONS.systemRegister.viewRiskRegister,
    PERMISSIONS.systemRegister.viewAssetRegister,
    PERMISSIONS.systemRegister.viewSop,
    PERMISSIONS.systemRegister.viewChangeManagement,
    PERMISSIONS.systemRegister.viewMonitoringReview,
    PERMISSIONS.systemRegister.viewIncidentManagement,
    PERMISSIONS.systemRegister.viewAccessProvision,
  ];
  /** Sidebar: tenant must have System Register; users need a module or card permission unless admin-class role. */
  const canSeeSystemRegisterNav =
    canSeeSystemRegisterService
    && (
      canBypassSystemRegisterPermission
      || sessionHasPermission(profile, PERMISSIONS.systemRegister.perform)
      || itGovernanceViewPermissions.some((code) => sessionHasPermission(profile, code))
    );
  const canSeePayrollNav = canSeePayroll;
  const canSeeContractNav = canSeeContract;
  const canManageTenants =
    role === "ADMIN" ||
    role === "SYSTEMADMIN" ||
    role === "MANAGER" ||
    role === "MAIN_ADMINISTRATOR" ||
    sessionHasPermission(profile, TENANT_ADMIN_PERMISSION);
  const canSeeAllSidebarOptions =
    role === "ADMIN" ||
    role === "SYSTEMADMIN" ||
    role === "MANAGER" ||
    role === "MAIN_ADMINISTRATOR";
  const canSeePermissionNav =
    role === "ADMIN" ||
    role === "SYSTEMADMIN";
  const canSeeAuditTrailNav =
    normalizedRole === "ADMIN"
    || normalizedRole === "SYSTEMADMIN"
    || normalizedRole === "SERVICEADMIN";
  const canSeeServiceAdminNav = normalizedRole === "SERVICEADMIN";
  const canSeeUserNav = normalizedRole === "USER";
  const canSeeTenantSettingsNav =
    hasRegistryTenant &&
    (normalizedRole === "SERVICEADMIN"
      || normalizedRole === "ADMIN"
      || normalizedRole === "SYSTEMADMIN");
  const sidebarRef = useRef(null);

  const closeSidebar = () => setIsSidebarOpen(false);

  const closeDrawerOnNavigate = () => {
    if (!isDesktopSidebar) closeSidebar();
  };

  const handleCloseSidebar = () => {
    closeSidebar();
  };

  const showSidebar = isDesktopSidebar || isSidebarOpen;

  useEffect(() => {
    if (isDesktopSidebar) {
      setIsSidebarOpen(true);
      return;
    }

    setIsSidebarOpen(false);
  }, [isDesktopSidebar, setIsSidebarOpen]);

  // Small screens: close drawer on route change or when leaving the desktop breakpoint.
  useEffect(() => {
    if (!isDesktopSidebar) {
      setIsSidebarOpen(false);
    }
  }, [location.pathname, isDesktopSidebar, setIsSidebarOpen]);

  useEffect(() => {
    const path = stripTenantPrefixFromPathname(location.pathname);
    if (path.startsWith("/groups")) setSelected("Groups");
    else if (path.startsWith("/permissions")) setSelected("Permissions");
    else if (path.startsWith("/user-audit-trials")) setSelected("Audit Trail");
    else if (path.startsWith("/tenant-users")) setSelected("Users");
    else if (path.startsWith("/services")) setSelected("Services");
    else if (path.startsWith("/payroll")) setSelected("Payroll");
    else if (path.startsWith("/contract")) setSelected("Contract");
    else if (path.startsWith("/system-register")) setSelected("IT Governance");
    else if (path.startsWith("/dashboard") || path === "/") setSelected("Dashboard");
    else if (path.startsWith("/tenants")) setSelected("Tenants Management");
    else if (path.startsWith("/settings")) setSelected("Settings");
  }, [location.pathname]);

  // Tap / click outside closes drawer on small / medium screens (touchstart helps real devices)
  useEffect(() => {
    const handleOutside = (event) => {
      if (isDesktopSidebar) return;
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        if (isSidebarOpen) {
          closeSidebar();
        }
      }
    };

    if (isSidebarOpen && !isDesktopSidebar) {
      document.addEventListener("mousedown", handleOutside);
      document.addEventListener("touchstart", handleOutside, { passive: true });
    }

    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("touchstart", handleOutside);
    };
  }, [isSidebarOpen, isDesktopSidebar]);

  if (!showSidebar) return null;

  const w = SIDEBAR_WIDTH_PX;

  return (
    <Box
      ref={sidebarRef}
      sx={{
        flexShrink: 0,
        width: w,
        maxWidth: w,
        height: "100vh",
        minHeight: "100vh",
        overflowY: "auto",
        overflowX: "hidden",
        backgroundColor: colors.primary[400],
        position: "fixed",
        top: 0,
        left: 0,
        zIndex: isDesktopSidebar ? 1200 : 1400,
      }}
    >
      <ProSidebar
        width={`${w}px`}
        backgroundColor={colors.primary[400]}
        rootStyles={{
          height: "100%",
          minHeight: "100vh",
          "& .ps-sidebar-container": {
            backgroundColor: colors.primary[400],
            height: "100%",
            minHeight: "100vh",
          },
          "& .ps-menu-button": {
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "flex-start",
            gap: "10px",
            padding: "8px 12px 8px 10px",
            minHeight: "auto",
            height: "auto",
            textAlign: "left",
            color: `${colors.grey[100]} !important`,
            "&:hover": {
              color: `${primaryMain} !important`,
              backgroundColor: `${alpha(primaryMain, 0.2)} !important`,
            },
          },
          "& .ps-menu-button.ps-active": {
            color: `${selectedText} !important`,
            backgroundColor: `${secondaryMain} !important`,
            fontWeight: 600,
          },
          "& .ps-menu-icon": {
            margin: 0,
            minWidth: "1.35rem",
            width: "1.35rem",
            height: "1.35rem",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            color: "inherit",
            "& svg": {
              fontSize: "1.15rem",
              display: "block",
            },
          },
          "& .ps-menu-button:hover .ps-menu-icon": {
            color: `${primaryMain} !important`,
          },
          "& .ps-menu-button.ps-active .ps-menu-icon": {
            color: `${selectedText} !important`,
          },
          "& .ps-submenu-content": {
            backgroundColor: colors.primary[400],
          },
        }}
      >
        <Menu>
          <MenuItem
            style={{
              margin: "6px 0 12px 0",
              color: colors.grey[100],
            }}
          >
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              position="relative"
              width="100%"
              px={1}
              gap={1}
            >
              <Typography
                variant="h3"
                color={colors.grey[100]}
                sx={{
                  fontSize: "1.1rem",
                  fontWeight: 700,
                  flex: 1,
                  minWidth: 0,
                  lineHeight: 1,
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  textAlign: "center",
                  px: { xs: 2, md: 0.5 },
                }}
              >
                {tenantDisplayName}
              </Typography>
              <IconButton
                onClick={handleCloseSidebar}
                aria-label="Close sidebar"
                size="small"
                sx={{
                  display: { xs: "inline-flex", md: "none" },
                  position: "absolute",
                  right: 0,
                  color: colors.grey[100],
                  "&:hover": {
                    color: primaryMain,
                    backgroundColor: alpha(primaryMain, 0.15),
                  },
                }}
              >
                <Cancel fontSize="small" />
              </IconButton>
            </Box>
          </MenuItem>

          <Box mb="14px" textAlign="center" px={0.5}>
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              sx={{ width: "100%", cursor: "pointer" }}
            >
              {tenantLogoSrc ? (
                <Box
                  component="img"
                  src={tenantLogoSrc}
                  alt=""
                  sx={{
                    display: "block",
                    width: "100%",
                    maxWidth: "100%",
                    maxHeight: 120,
                    mx: "auto",
                    objectFit: "contain",
                  }}
                />
              ) : null}
            </Box>

            {/* <Typography variant="h5" color={colors.greenAccent[600]} sx={{ fontSize: "0.7rem", mt: 0.75, lineHeight: 1.3 }}>
              Manage tenants
            </Typography> */}
          </Box>

          <Box paddingLeft="6%">
            {canSeeAllSidebarOptions && (
              <>
                <Item title="Dashboard" to={tp("/dashboard")} icon={<HomeOutlined />} selected={selected} setSelected={setSelected} closeSidebar={closeDrawerOnNavigate} />
                <Item title="Tenants Management" to={tp("/tenants")} icon={<PersonOutlined />} selected={selected} setSelected={setSelected} closeSidebar={closeDrawerOnNavigate} />
                <Item
                  title="Services"
                  to={tp("/services")}
                  icon={<DesignServicesOutlined />}
                  selected={selected}
                  setSelected={setSelected}
                  closeSidebar={closeDrawerOnNavigate}
                />
                {canSeePayrollNav && (
                  <Item
                    title="Payroll"
                    to={tp("/payroll")}
                    icon={<PaymentsOutlined />}
                    selected={selected}
                    setSelected={setSelected}
                    closeSidebar={closeDrawerOnNavigate}
                  />
                )}
                {canSeeContractNav && (
                  <Item
                    title="Contract"
                    to={tp("/contract")}
                    icon={<Assignment />}
                    selected={selected}
                    setSelected={setSelected}
                    closeSidebar={closeDrawerOnNavigate}
                  />
                )}
                {canSeeSystemRegisterNav && (
                  <Item
                    title="IT Governance"
                    to={tp("/system-register")}
                    icon={<AppRegistrationOutlined />}
                    selected={selected}
                    setSelected={setSelected}
                    closeSidebar={closeDrawerOnNavigate}
                  />
                )}
                {hasRegistryTenant && (
                  <Item
                    title="Users"
                    to={tp(`/tenant-users/${registryTenantId}`)}
                    linkState={tenantUsersLinkState}
                    icon={<PeopleOutlined />}
                    selected={selected}
                    setSelected={setSelected}
                    closeSidebar={closeDrawerOnNavigate}
                  />
                )}
                {canSeeTenantSettingsNav && (
                  <Item
                    title="Settings"
                    to={tp("/settings")}
                    icon={<SettingsOutlined />}
                    selected={selected}
                    setSelected={setSelected}
                    closeSidebar={closeDrawerOnNavigate}
                  />
                )}
                <SubMenu
                  label={
                    <Typography
                      component="span"
                      sx={{
                        fontSize: "0.8rem",
                        lineHeight: 1.35,
                        fontWeight: 600,
                        textAlign: "left",
                        color: "inherit",
                      }}
                    >
                      Access Control
                    </Typography>
                  }
                  icon={<AdminPanelSettingsOutlined sx={{ fontSize: "1.15rem" }} />}
                  defaultOpen={
                    location.pathname.startsWith("/groups") ||
                    location.pathname.startsWith("/permissions") ||
                    location.pathname.startsWith("/user-audit-trials")
                  }
                  active={
                    location.pathname.startsWith("/groups") ||
                    location.pathname.startsWith("/permissions") ||
                    location.pathname.startsWith("/user-audit-trials")
                  }
                >
                  <Item title="Groups" to={tp("/groups")} icon={<GroupsOutlined />} selected={selected} setSelected={setSelected} closeSidebar={closeDrawerOnNavigate} />
                  {canSeePermissionNav ? (
                    <Item title="Permissions" to={tp("/permissions")} icon={<VpnKeyOutlined />} selected={selected} setSelected={setSelected} closeSidebar={closeDrawerOnNavigate} />
                  ) : null}
                  {canSeeAuditTrailNav ? (
                    <Item title="Audit Trail" to={tp("/user-audit-trials")} icon={<HistoryOutlined />} selected={selected} setSelected={setSelected} closeSidebar={closeDrawerOnNavigate} />
                  ) : null}
                </SubMenu>
              </>
            )}

            {canSeeServiceAdminNav && (
              <>
                <Item title="Dashboard" to={tp("/dashboard")} icon={<HomeOutlined />} selected={selected} setSelected={setSelected} closeSidebar={closeDrawerOnNavigate} />
                {canSeePayrollNav && (
                  <Item
                    title="Payroll"
                    to={tp("/payroll")}
                    icon={<PaymentsOutlined />}
                    selected={selected}
                    setSelected={setSelected}
                    closeSidebar={closeDrawerOnNavigate}
                  />
                )}
                {canSeeContractNav && (
                  <Item
                    title="Contract"
                    to={tp("/contract")}
                    icon={<Assignment />}
                    selected={selected}
                    setSelected={setSelected}
                    closeSidebar={closeDrawerOnNavigate}
                  />
                )}
                {canSeeSystemRegisterNav && (
                  <Item
                    title="IT Governance"
                    to={tp("/system-register")}
                    icon={<AppRegistrationOutlined />}
                    selected={selected}
                    setSelected={setSelected}
                    closeSidebar={closeDrawerOnNavigate}
                  />
                )}
                {hasRegistryTenant && (
                  <Item
                    title="Users"
                    to={tp(`/tenant-users/${registryTenantId}`)}
                    linkState={tenantUsersLinkState}
                    icon={<PeopleOutlined />}
                    selected={selected}
                    setSelected={setSelected}
                    closeSidebar={closeDrawerOnNavigate}
                  />
                )}
                {canSeeTenantSettingsNav && (
                  <Item
                    title="Settings"
                    to={tp("/settings")}
                    icon={<SettingsOutlined />}
                    selected={selected}
                    setSelected={setSelected}
                    closeSidebar={closeDrawerOnNavigate}
                  />
                )}
                <SubMenu
                  label={
                    <Typography
                      component="span"
                      sx={{
                        fontSize: "0.8rem",
                        lineHeight: 1.35,
                        fontWeight: 600,
                        textAlign: "left",
                        color: "inherit",
                      }}
                    >
                      Access Control
                    </Typography>
                  }
                  icon={<AdminPanelSettingsOutlined sx={{ fontSize: "1.15rem" }} />}
                  defaultOpen={
                    location.pathname.startsWith("/groups") ||
                    location.pathname.startsWith("/permissions") ||
                    location.pathname.startsWith("/user-audit-trials")
                  }
                  active={
                    location.pathname.startsWith("/groups") ||
                    location.pathname.startsWith("/permissions") ||
                    location.pathname.startsWith("/user-audit-trials")
                  }
                >
                  <Item title="Groups" to={tp("/groups")} icon={<GroupsOutlined />} selected={selected} setSelected={setSelected} closeSidebar={closeDrawerOnNavigate} />
                  {canSeePermissionNav ? (
                    <Item title="Permissions" to={tp("/permissions")} icon={<VpnKeyOutlined />} selected={selected} setSelected={setSelected} closeSidebar={closeDrawerOnNavigate} />
                  ) : null}
                  {canSeeAuditTrailNav ? (
                    <Item title="Audit Trail" to={tp("/user-audit-trials")} icon={<HistoryOutlined />} selected={selected} setSelected={setSelected} closeSidebar={closeDrawerOnNavigate} />
                  ) : null}
                </SubMenu>
              </>
            )}

            {canSeeUserNav && (
              <>
                <Item title="Dashboard" to={tp("/dashboard")} icon={<HomeOutlined />} selected={selected} setSelected={setSelected} closeSidebar={closeDrawerOnNavigate} />
                {canSeePayrollNav && (
                  <Item
                    title="Payroll"
                    to={tp("/payroll")}
                    icon={<PaymentsOutlined />}
                    selected={selected}
                    setSelected={setSelected}
                    closeSidebar={closeDrawerOnNavigate}
                  />
                )}
                {canSeeContractNav && (
                  <Item
                    title="Contract"
                    to={tp("/contract")}
                    icon={<Assignment />}
                    selected={selected}
                    setSelected={setSelected}
                    closeSidebar={closeDrawerOnNavigate}
                  />
                )}
                {canSeeSystemRegisterNav && (
                  <Item
                    title="IT Governance"
                    to={tp("/system-register")}
                    icon={<AppRegistrationOutlined />}
                    selected={selected}
                    setSelected={setSelected}
                    closeSidebar={closeDrawerOnNavigate}
                  />
                )}
              </>
            )}

            {!canSeeAllSidebarOptions &&
              !canSeeServiceAdminNav &&
              !canSeeUserNav &&
              canManageTenants && (
                <>
                  <Item title="Dashboard" to={tp("/dashboard")} icon={<HomeOutlined />} selected={selected} setSelected={setSelected} closeSidebar={closeDrawerOnNavigate} />
                  {canSeePayrollNav && (
                    <Item
                      title="Payroll"
                      to={tp("/payroll")}
                      icon={<PaymentsOutlined />}
                      selected={selected}
                      setSelected={setSelected}
                      closeSidebar={closeDrawerOnNavigate}
                    />
                  )}
                  {canSeeContractNav && (
                    <Item
                      title="Contract"
                      to={tp("/contract")}
                      icon={<Assignment />}
                      selected={selected}
                      setSelected={setSelected}
                      closeSidebar={closeDrawerOnNavigate}
                    />
                  )}
                  {canSeeSystemRegisterNav && (
                    <Item
                      title="IT Governance"
                      to={tp("/system-register")}
                      icon={<AppRegistrationOutlined />}
                      selected={selected}
                      setSelected={setSelected}
                      closeSidebar={closeDrawerOnNavigate}
                    />
                  )}
                  {hasRegistryTenant && (
                    <Item
                      title="Users"
                      to={tp(`/tenant-users/${registryTenantId}`)}
                      linkState={tenantUsersLinkState}
                      icon={<PeopleOutlined />}
                      selected={selected}
                      setSelected={setSelected}
                      closeSidebar={closeDrawerOnNavigate}
                    />
                  )}
                </>
              )}
          </Box>
        </Menu>
      </ProSidebar>
    </Box>
  );
};

export default SidebarComponent;